import { useEffect } from 'react'
import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import { fullJitter, POLICIES } from '@one-deployment/shared'
import type { LiveEvent } from '@one-deployment/api'
import { useLive } from '@/stores/live'
import { applyLiveEvent } from './cacheMap'

/**
 * One socket per topic (01 §4): `/api/live?topic=project:<key>` or
 * `workspace:<id>`, opened by the screen that shows it and closed when the
 * screen goes. The session cookie authenticates the upgrade (same origin). A
 * reconnect asks for `from_seq=<last seq>`; the hub replays what it still holds.
 * `global` carries alerts, server and container status, inbox and activity
 * nudges, filtered server-side to the projects the person can see; the shell
 * holds it open for every signed-in screen. A 4408 close means that filter
 * expired and is an ordinary reconnect, which recomputes it.
 */
export class LiveTopic {
  private ws: WebSocket | null = null
  private lastSeq = 0
  private attempt = 0
  private timer: ReturnType<typeof setTimeout> | null = null
  private pingTimer: ReturnType<typeof setInterval> | null = null
  private stopped = false

  constructor(
    private readonly qc: QueryClient,
    readonly topic: string,
    private readonly url = defaultUrl(),
  ) {}

  start(): void {
    this.stopped = false
    this.connect()
    window.addEventListener('online', this.onOnline)
  }

  stop(): void {
    this.stopped = true
    window.removeEventListener('online', this.onOnline)
    if (this.timer) clearTimeout(this.timer)
    if (this.pingTimer) clearInterval(this.pingTimer)
    this.ws?.close(1000)
    this.ws = null
    useLive.getState().setTopic(this.topic, null)
  }

  private onOnline = () => {
    if (this.ws?.readyState !== WebSocket.OPEN) {
      if (this.timer) clearTimeout(this.timer)
      this.connect()
    }
  }

  private connect(): void {
    if (this.stopped) return
    const live = useLive.getState()
    live.setTopic(this.topic, { status: this.attempt === 0 ? 'connecting' : 'reconnecting', attempt: this.attempt })
    const ws = new WebSocket(`${this.url}?topic=${encodeURIComponent(this.topic)}&from_seq=${this.lastSeq}`)
    this.ws = ws

    ws.onopen = () => {
      const reconnected = this.attempt > 0
      this.attempt = 0
      useLive.getState().setTopic(this.topic, { status: 'open', attempt: 0 })
      useLive.getState().touch()
      // The hub keeps only its last few hundred events; whatever fell out of
      // that ring while we were away is fetched rather than assumed.
      if (reconnected) applyLiveEvent(this.qc, { topic: this.topic, seq: 0, event: 'project.updated', data: {} })
      if (this.pingTimer) clearInterval(this.pingTimer)
      this.pingTimer = setInterval(() => ws.readyState === WebSocket.OPEN && ws.send('ping'), 25_000)
    }
    ws.onmessage = (msg) => {
      if (msg.data === 'pong') return
      let e: LiveEvent
      try {
        e = JSON.parse(String(msg.data)) as LiveEvent
      } catch {
        return
      }
      // At-least-once from the hub: a seq we have already applied is a replay.
      if (e.seq <= this.lastSeq) return
      this.lastSeq = e.seq
      const effect = applyLiveEvent(this.qc, e)
      useLive.getState().touch()
      if (effect.toast) useLive.getState().pushToast(effect.toast)
    }
    ws.onclose = (ev) => {
      if (this.pingTimer) clearInterval(this.pingTimer)
      if (this.stopped) return
      // 4401 / a refused upgrade for a gone session: reconnecting would loop.
      if (ev.code === 4401) {
        useLive.getState().setTopic(this.topic, { status: 'closed', attempt: 0 })
        void this.qc.invalidateQueries({ queryKey: ['me'] })
        return
      }
      this.scheduleReconnect()
    }
    ws.onerror = () => ws.close()
  }

  private scheduleReconnect(): void {
    this.attempt += 1
    useLive.getState().setTopic(this.topic, { status: 'reconnecting', attempt: this.attempt })
    // Full jitter so every tab of every person does not reconnect in the same
    // millisecond after a Worker deploy.
    const delay = Math.max(250, fullJitter(this.attempt, POLICIES.reconnect))
    this.timer = setTimeout(() => this.connect(), delay)
  }
}

function defaultUrl(): string {
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${proto}//${location.host}/api/live`
}

/** Hold a topic's socket open for as long as the calling screen is mounted. */
export function useLiveTopic(topic: string | null | undefined): void {
  const qc = useQueryClient()
  useEffect(() => {
    if (!topic) return
    const t = new LiveTopic(qc, topic)
    t.start()
    return () => t.stop()
  }, [qc, topic])
}

export const GLOBAL_TOPIC = 'global'

/**
 * Options for a list the global topic keeps current. Its events only say
 * "this changed", so the poll stays as a fallback: the screen's own interval
 * while the socket is down, five minutes while it is healthy.
 */
export function useGlobalQueryOpts(interval: number): { refetchInterval: number } {
  const open = useLive((s) => s.topics[GLOBAL_TOPIC]?.status === 'open')
  return { refetchInterval: open ? 5 * 60_000 : interval }
}

/**
 * Options for a query a topic keeps current: never stale while that socket is
 * up, polled every 60 s while it is down or when there is no topic for it at
 * all (03 §8 degraded mode).
 */
export function useLiveQueryOpts(topic?: string | null): { staleTime: number; refetchInterval: number | false } {
  const open = useLive((s) => (topic ? s.topics[topic]?.status === 'open' : false))
  return { staleTime: open ? Infinity : 30_000, refetchInterval: open ? false : 60_000 }
}
